// =====================================================================
// Freeform editor timeline
// =====================================================================
// The editor, the in-browser preview and the render worker all consume
// this document. `compileEditorTimeline` is the only translation into the
// Shotstack IR, so preview and export describe the same cut.
//
// Timing: clips are packed in array order. A crossfade overlaps the next
// clip with the previous one by the transition length (capped at half of
// either clip) so the fade does not open a gap or extend the timeline.
// =====================================================================

import type {
  LogoAnimationStyle,
  RenderFormat,
  TimelineAudioClip,
  TimelineIR,
  TimelineScene,
  TimelineTextOverlay,
} from './index.ts';
import { DEFAULT_MOTION, motionFromLogoAnimation, normaliseMotion, type ElementMotion } from './motion.ts';
import { resolveTextStyle, type TextStyle } from './text.ts';
import { activeClipGrade, type ClipGrade } from './grade.ts';
import {
  coalesceSpeedPieces,
  hasSpeedChange,
  normaliseSpeedZones,
  SPEED_ZONE_OPEN_END,
  speedOutputSeconds,
  speedSourceAt,
  windowSpeedPieces,
  type SpeedZone,
} from './speed.ts';

/** Legacy beat. Used when opening template projects so their timing does not change. */
export const DEFAULT_PHOTO_SECONDS = 3;
/** Photos and floor plans added in the editor hold for this long, with no movement. */
export const DEFAULT_STILL_SECONDS = 5;
export const DEFAULT_CROSSFADE_SECONDS = 0.5;
export const DEFAULT_BLANK_SECONDS = 3;
export const DEFAULT_BLANK_COLOR = '#0f172a';
export const MIN_CLIP_SECONDS = 0.25;
export const DEFAULT_VOICE_DUCK = 0.3;
/** Fraction of the frame a logo fills on a colour plate. */
export const DEFAULT_PLATE_LOGO_SCALE = 0.92;
export const MIN_PLATE_LOGO_SCALE = 0.3;
export const MAX_PLATE_LOGO_SCALE = 1.25;
/** Corner watermark drawn over the whole video. */
export const MIN_OVERLAY_LOGO_SCALE = 0.08;
export const MAX_OVERLAY_LOGO_SCALE = 0.95;
/** Smallest a photo or clip can be scaled within the frame. */
export const MIN_CLIP_SCALE = 0.2;

export type FitMode = 'cover' | 'contain';
/** `crossfade` is the older name for `dissolve` and still overlaps the same way. */
export type CutStyle = 'cut' | 'crossfade' | 'dissolve' | 'wipe' | 'slide' | 'zoom' | 'reveal' | 'carousel';
export type TextAlign = 'left' | 'center' | 'right';
export type TextRole =
  | 'free'
  | 'title'
  | 'heading'
  | 'subheading'
  | 'body'
  | 'detail'
  | 'caption'
  | 'lowerThird'
  | 'endcard'
  | 'address'
  | 'price'
  | 'agent';

export interface EditorClip {
  id: string;
  assetId: string;
  /** Solid colour plate. Used for an opening or end logo reveal. */
  blank?: boolean;
  /** Hex colour for a blank plate. */
  background?: string;
  /** Draw the uploaded logo on this plate. */
  showLogo?: boolean;
  /** How much of the frame the plate logo fills. Omitted plates use the default. */
  logoScale?: number;
  logoAnimation?: LogoAnimationStyle;
  /** Plate logo motion. Older plates fall back to `logoAnimation`. */
  logoMotion?: ElementMotion;
  /** Plate logo centre as a fraction of the frame. Omitted plates are centred. */
  logoX?: number;
  logoY?: number;
  trimStart: number;
  duration: number;
  fit: FitMode;
  /** -1..1, 0 is centred. Positive x reveals more of the media's right side. */
  offsetX: number;
  /** -1..1, 0 is centred. Positive y reveals more of the media's top. */
  offsetY: number;
  /**
   * Fraction of the frame the clip fills. Omitted or 1 = full frame. Below 1
   * the whole picture shows on the background and the offsets move it, as a
   * fraction of the frame from centre.
   */
  scale?: number;
  transitionIn: CutStyle;
  transitionDuration: number;
  /**
   * Empty seconds before this clip after it has been pulled away from the
   * one before. No transition plays across a gap.
   */
  gapBefore?: number;
  /** 0–1 original soundtrack. Photos ignore this. */
  volume: number;
  muted: boolean;
  /**
   * Video only. Parts of the footage, in seconds into the source file, that
   * play faster or slower. With zones, `duration` is still the time the clip
   * takes on the timeline; the footage it covers ends at `clipSourceEnd`.
   */
  speedZones?: SpeedZone[];
  /**
   * The photo this scene was made from while `assetId` points at its
   * ImageMotion clip. Unset on ordinary scenes.
   */
  sourceAssetId?: string;
  /** ImageMotion animation requested for this scene's photo. */
  animation?: SceneAnimation;
  /** VideoMotion processing that produced `assetId` from an uploaded clip. */
  videoMotion?: SceneVideoMotion;
  /**
   * Video only. Curves (brighten) and white balance. Omitted when the
   * picture is unchanged. The preview and the export use the same grade.
   */
  grade?: ClipGrade;
}

/** Still framing kept while a scene plays its animation, so reverting restores it. */
export interface StillFraming {
  duration: number;
  fit: FitMode;
  offsetX: number;
  offsetY: number;
}

export interface SceneAnimation {
  /** propertycut_jobs row. Status lives on the row, not here. */
  jobId: string;
  /** Generated clip once the job has finished. */
  assetId: string | null;
  /** True while the scene plays the generated clip instead of the photo. */
  active: boolean;
  still?: StillFraming;
}

export interface SceneVideoMotion {
  /** The uploaded footage before processing. */
  sourceAssetId: string;
  /** propertycut_jobs row. */
  jobId: string;
}

/** Optional typography; omitted fields use `DEFAULT_TEXT_STYLE`. */
export interface EditorText extends Partial<TextStyle> {
  id: string;
  role: TextRole;
  text: string;
  start: number;
  duration: number;
  fontSize: number;
  color: string;
  align: TextAlign;
  background: string | null;
  x: number;
  y: number;
  motion?: ElementMotion;
}

export type AudioRole = 'music' | 'voiceover';

/**
 * Named on clips saved while an external catalogue was wired up.
 * No catalogue is connected; those clips cannot be previewed or exported.
 */
export type MusicProvider = 'epidemic';

export interface EditorAudio {
  id: string;
  /** Omitted = music. A voiceover plays on top and lowers music and clip sound. */
  role?: AudioRole;
  assetId: string | null;
  musicId: string | null;
  /** Set on clips from a removed external catalogue. */
  provider?: MusicProvider;
  providerTrackId?: string | null;
  start: number;
  trimStart: number;
  duration: number;
  /** 0–1 */
  volume: number;
  fadeIn: number;
  fadeOut: number;
}

export interface EditorLogo {
  visible: boolean;
  x: number;
  y: number;
  scale: number;
  motion?: ElementMotion;
}

export interface EditorState {
  version: 1;
  clips: EditorClip[];
  texts: EditorText[];
  audios: EditorAudio[];
  logo: EditorLogo;
  /** 0–1 level music and clip sound drop to while a voiceover plays. */
  voiceDuck?: number;
}

export interface LaidOutClip extends EditorClip {
  start: number;
  /** Seconds this clip overlaps the previous one. 0 for a cut. */
  overlap: number;
}

export interface MediaRef {
  id: string;
  url: string;
  kind: 'photo' | 'video' | 'floor_plan' | 'audio';
  durationSeconds: number | null;
  name: string | null;
}

export interface CompileIssue {
  assetId?: string;
  /** Set when a library track could not be resolved. */
  musicKey?: string;
  name: string;
  message: string;
}

export interface EditorHistory {
  past: EditorState[];
  present: EditorState;
  future: EditorState[];
}

const DIMS: Record<RenderFormat, { width: number; height: number; aspectRatio: '16:9' | '9:16' | '1:1' }> = {
  vertical: { width: 1080, height: 1920, aspectRatio: '9:16' },
  square: { width: 1080, height: 1080, aspectRatio: '1:1' },
  landscape: { width: 1920, height: 1080, aspectRatio: '16:9' },
};

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

export function roundTime(n: number): number {
  return Math.round(n * 100) / 100;
}

export function createEditorId(prefix: string): string {
  const rand =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}-${rand}`;
}

export function emptyEditorState(): EditorState {
  return {
    version: 1,
    clips: [],
    texts: [],
    audios: [],
    logo: { visible: false, x: 0.78, y: 0.82, scale: 0.28 },
  };
}

export function layOutClips(clips: EditorClip[]): { clips: LaidOutClip[]; duration: number } {
  const laid: LaidOutClip[] = [];
  let cursor = 0;
  for (let i = 0; i < clips.length; i++) {
    const clip = clips[i];
    const duration = Math.max(MIN_CLIP_SECONDS, clip.duration);
    const gap = clipGap(clip);
    let overlap = 0;
    if (i > 0 && gap === 0 && clip.transitionIn !== 'cut') {
      const prev = laid[i - 1];
      const cap = Math.min(prev.duration, duration) / 2;
      overlap = Math.min(Math.max(0, clip.transitionDuration), cap);
    }
    const start = gap > 0 ? roundTime(cursor + gap) : i === 0 ? 0 : roundTime(Math.max(0, cursor - overlap));
    laid.push({
      ...clip,
      duration: roundTime(duration),
      start,
      overlap: roundTime(overlap),
    });
    cursor = start + duration;
  }
  return { clips: laid, duration: roundTime(cursor) };
}

/** Gaps shorter than this are treated as no gap, so the clips stay joined. */
const MIN_GAP_SECONDS = 0.02;

export function clipGap(clip: Pick<EditorClip, 'gapBefore'>): number {
  const gap = clip.gapBefore ?? 0;
  return Number.isFinite(gap) && gap >= MIN_GAP_SECONDS ? roundTime(gap) : 0;
}

function withGap(clip: EditorClip, gap: number): EditorClip {
  const { gapBefore: _old, ...rest } = clip;
  return gap >= MIN_GAP_SECONDS ? { ...rest, gapBefore: roundTime(gap) } : rest;
}

/**
 * Re-derive every gap so each clip starts as close to `wanted` (by id) as it
 * can without starting before the end of the clip before it. A clip that
 * can't stay put is pushed along, joined to the one before.
 */
function placeClips(clips: EditorClip[], wanted: ReadonlyMap<string, number>): EditorClip[] {
  const out: EditorClip[] = [];
  let cursor = 0;
  for (let i = 0; i < clips.length; i++) {
    const clip = clips[i];
    const duration = Math.max(MIN_CLIP_SECONDS, clip.duration);
    const target = wanted.get(clip.id);
    let gap = target === undefined ? clipGap(clip) : Math.max(0, target - cursor);
    if (gap < MIN_GAP_SECONDS) gap = 0;
    let start = cursor + gap;
    if (gap === 0 && i > 0 && clip.transitionIn !== 'cut') {
      const prevDuration = Math.max(MIN_CLIP_SECONDS, clips[i - 1].duration);
      start = cursor - Math.min(Math.max(0, clip.transitionDuration), Math.min(prevDuration, duration) / 2);
    }
    out.push(clipGap(clip) === roundTime(gap) ? clip : withGap(clip, gap));
    cursor = roundTime(Math.max(0, start)) + roundTime(duration);
  }
  return out;
}

function startsById(clips: EditorClip[]): Map<string, number> {
  return new Map(layOutClips(clips).clips.map((clip) => [clip.id, clip.start]));
}

/**
 * Move a clip to `start` seconds without moving the others, leaving a gap
 * where it was. It can't go before the end of the clip before it, and it
 * pushes later clips along if it runs into them. Within `snapSeconds` of a
 * neighbour it joins up to it again.
 */
export function placeClip(state: EditorState, clipId: string, start: number, snapSeconds = 0): EditorState {
  const laid = layOutClips(state.clips).clips;
  const index = laid.findIndex((clip) => clip.id === clipId);
  if (index < 0) return state;
  const clip = laid[index];
  const prev = laid[index - 1];
  const next = laid[index + 1];
  const prevEnd = prev ? prev.start + prev.duration : 0;
  let target = Math.max(prevEnd, start);
  if (target - prevEnd <= snapSeconds) target = prevEnd;
  else if (next && Math.abs(target + clip.duration - next.start) <= snapSeconds) target = next.start - clip.duration;
  const wanted = startsById(state.clips);
  wanted.set(clipId, target);
  const clips = placeClips(state.clips, wanted);
  return clips.every((c, i) => c === state.clips[i]) ? state : { ...state, clips };
}

/**
 * Apply an edit to one clip while every clip after it keeps its place on
 * the timeline, so trimming opens a gap instead of sliding the rest along.
 * `start` moves the edited clip itself, for trims from its left edge.
 */
export function holdClipPositions(
  before: EditorState,
  after: EditorState,
  clipId: string,
  start?: number,
): EditorState {
  if (after === before) return before;
  const wanted = startsById(before.clips);
  if (start !== undefined) wanted.set(clipId, start);
  const clips = placeClips(after.clips, wanted);
  return clips.every((c, i) => c === after.clips[i]) ? after : { ...after, clips };
}

/** Remove the gap before a clip so it joins the one before; later clips move up. */
export function closeClipGap(state: EditorState, clipId: string): EditorState {
  const current = state.clips.find((clip) => clip.id === clipId);
  if (!current || clipGap(current) === 0) return state;
  return replaceClip(state, clipId, withGap(current, 0));
}

export function closeAllClipGaps(state: EditorState): EditorState {
  if (!state.clips.some((clip) => clipGap(clip) > 0)) return state;
  return { ...state, clips: state.clips.map((clip) => withGap(clip, 0)) };
}

export function visualDuration(state: EditorState): number {
  return layOutClips(state.clips).duration;
}

/** Timeline length the preview and the export both use. */
export function projectDuration(state: EditorState): number {
  let end = visualDuration(state);
  for (const text of state.texts) {
    if (!text.text.trim()) continue;
    end = Math.max(end, text.start + Math.max(MIN_CLIP_SECONDS, text.duration));
  }
  for (const audio of state.audios) {
    end = Math.max(end, audio.start + Math.max(MIN_CLIP_SECONDS, audio.duration));
  }
  return roundTime(end);
}

export interface MixSpan {
  start: number;
  end: number;
  volume: number;
}

/**
 * Divide each source by how many audible sources it overlaps, so two full
 * tracks cannot sum past their own level.
 */
export function limitOverlapVolumes(spans: MixSpan[]): number[] {
  return spans.map((span) => {
    if (span.volume <= 0 || span.end <= span.start) return 0;
    let count = 0;
    for (const other of spans) {
      if (other.volume <= 0 || other.end <= other.start) continue;
      if (span.start < other.end - 0.001 && other.start < span.end - 0.001) count += 1;
    }
    return span.volume / Math.max(1, count);
  });
}

/** Shotstack's default transition is about a second; Fast and Slow bracket it. */
function paced(base: string, seconds: number): string {
  if (seconds <= 0.35) return `${base}Fast`;
  if (seconds >= 0.8) return `${base}Slow`;
  return base;
}

/**
 * Shotstack in/out names for a join. Cuts have no blend.
 * Slide and carousel used to take opposite directions. Shotstack's slide
 * only nudges while it fades, and the two directions cancelled, so those
 * joins looked like a dissolve. A carousel moves the whole frame, and the
 * same direction on the way in and the way out is one push.
 */
function transitionPair(style: CutStyle, seconds: number): { in: string; out: string } | undefined {
  if (style === 'cut') return undefined;
  const base =
    style === 'wipe' ? 'wipeRight'
    : style === 'slide' ? 'carouselLeft'
    : style === 'zoom' ? 'zoom'
    : style === 'reveal' ? 'reveal'
    : style === 'carousel' ? 'carouselUp'
    : 'fade';
  const name = paced(base, seconds);
  return { in: name, out: name };
}

/** Motion for a plate logo, reading plates saved before motion controls existed. */
export function plateLogoMotion(clip: Pick<EditorClip, 'logoMotion' | 'logoAnimation'>): ElementMotion {
  return clip.logoMotion
    ? normaliseMotion(clip.logoMotion)
    : motionFromLogoAnimation(clip.logoAnimation);
}

/** Corner logo and text default to no motion, matching how they always appeared. */
const STILL_MOTION: ElementMotion = { ...DEFAULT_MOTION, style: 'none' };

export function overlayMotion(motion: ElementMotion | undefined): ElementMotion {
  return normaliseMotion(motion, STILL_MOTION);
}

function mergeRanges(ranges: Array<{ start: number; end: number }>): Array<{ start: number; end: number }> {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [];
  for (const r of sorted) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end + 0.01) last.end = Math.max(last.end, r.end);
    else merged.push({ ...r });
  }
  return merged;
}

/**
 * Split a music clip at voiceover edges so the parts under a voiceover play
 * at the duck level. Fades stay on the clip's real start and end.
 */
function duckUnderVoice(
  clip: TimelineAudioClip,
  voice: Array<{ start: number; end: number }>,
  duck: number,
): TimelineAudioClip[] {
  const end = clip.start + clip.length;
  const cuts = new Set<number>([clip.start, end]);
  for (const r of voice) {
    if (r.start > clip.start && r.start < end) cuts.add(r.start);
    if (r.end > clip.start && r.end < end) cuts.add(r.end);
  }
  const points = [...cuts].sort((a, b) => a - b);
  const parts: TimelineAudioClip[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (b - a < 0.01) continue;
    const mid = (a + b) / 2;
    const ducked = voice.some((r) => mid >= r.start && mid < r.end);
    parts.push({
      ...clip,
      start: roundTime(a),
      length: roundTime(b - a),
      trim: roundTime(clip.trim + (a - clip.start)),
      volume: roundTime(ducked ? clip.volume * duck : clip.volume),
      fadeIn: a === clip.start ? clip.fadeIn : 0,
      fadeOut: b === end ? clip.fadeOut : 0,
    });
  }
  return parts;
}

/** Continuous stretches of photos and clips, skipping colour plates. */
function footageRanges(scenes: TimelineScene[]): Array<{ start: number; length: number }> {
  const ranges: Array<{ start: number; end: number }> = [];
  for (const scene of scenes) {
    if (scene.kind === 'logoBumper') continue;
    const end = scene.start + scene.length;
    const last = ranges[ranges.length - 1];
    if (last && scene.start <= last.end + 0.01) last.end = Math.max(last.end, end);
    else ranges.push({ start: scene.start, end });
  }
  return ranges.map((r) => ({ start: roundTime(r.start), length: roundTime(Math.max(MIN_CLIP_SECONDS, r.end - r.start)) }));
}

/** Key into `musicUrls` / `sourceSeconds` for a library track, or null for uploads and removed catalogues. */
export function musicSourceKey(audio: Pick<EditorAudio, 'musicId' | 'provider'>): string | null {
  if (audio.provider) return null;
  return audio.musicId || null;
}

/** Why a removed-catalogue clip cannot be used, or null for library tracks and uploads. */
export function providerAudioProblem(audio: Pick<EditorAudio, 'provider'>): string | null {
  if (!audio.provider) return null;
  return 'This music track is no longer available. Remove it and choose a royalty-free track or upload your own.';
}

/**
 * Keeps a music clip inside its source: trim cannot pass the end and the clip cannot run past it.
 * Unknown source length leaves the clip as it is. The track itself is never changed.
 */
export function clampAudioToSource(
  audio: Pick<EditorAudio, 'trimStart' | 'duration'>,
  sourceSeconds: number | null | undefined,
): { trimStart: number; duration: number } {
  let trimStart = Math.max(0, Number.isFinite(audio.trimStart) ? audio.trimStart : 0);
  let duration = Math.max(MIN_CLIP_SECONDS, Number.isFinite(audio.duration) ? audio.duration : MIN_CLIP_SECONDS);
  if (sourceSeconds && sourceSeconds > 0) {
    trimStart = Math.min(trimStart, Math.max(0, sourceSeconds - MIN_CLIP_SECONDS));
    duration = Math.min(duration, Math.max(MIN_CLIP_SECONDS, sourceSeconds - trimStart));
  }
  return { trimStart, duration };
}

export function compileEditorTimeline(args: {
  state: EditorState;
  format: RenderFormat;
  media: MediaRef[];
  musicUrls?: Record<string, string>;
  /** Source length per `musicSourceKey`, used to keep trims inside the track. */
  sourceSeconds?: Record<string, number>;
  logoUrl?: string | null;
}): { ir: TimelineIR; issues: CompileIssue[] } {
  const dims = DIMS[args.format];
  const byId = new Map(args.media.map((m) => [m.id, m]));
  const issues: CompileIssue[] = [];
  const laid = layOutClips(args.state.clips);
  const scenes: TimelineScene[] = [];

  const logoUrl = args.logoUrl?.trim() || null;

  for (const clip of laid.clips) {
    if (clip.blank) {
      const transition = clip.overlap > 0 ? transitionPair(clip.transitionIn, clip.overlap) : undefined;
      if (transition && scenes.length > 0) {
        const prev = scenes[scenes.length - 1];
        prev.transition = { in: prev.transition?.in ?? transition.out, out: transition.out };
      }
      scenes.push({
        id: clip.id,
        kind: 'logoBumper',
        start: clip.start,
        length: clip.duration,
        brandColor: clip.background || DEFAULT_BLANK_COLOR,
        plate: true,
        transition,
        bumper: clip.showLogo && logoUrl
          ? {
              logoUrl,
              animation: clip.logoAnimation || 'fadeIn',
              motion: plateLogoMotion(clip),
              scale: clamp(clip.logoScale ?? DEFAULT_PLATE_LOGO_SCALE, MIN_PLATE_LOGO_SCALE, MAX_PLATE_LOGO_SCALE),
              x: clamp(clip.logoX ?? 0.5, 0, 1),
              y: clamp(clip.logoY ?? 0.5, 0, 1),
            }
          : undefined,
      });
      continue;
    }
    const media = byId.get(clip.assetId);
    if (!media || !media.url) {
      issues.push({
        assetId: clip.assetId,
        name: media?.name || 'Untitled file',
        message: `${media?.name || 'A file'} could not be loaded. Remove it or upload it again.`,
      });
      continue;
    }
    if (media.kind === 'audio') continue;
    const kind = media.kind === 'video' ? 'video' : media.kind === 'floor_plan' ? 'floorPlan' : 'photo';
    const transition = clip.overlap > 0 ? transitionPair(clip.transitionIn, clip.overlap) : undefined;
    if (transition && scenes.length > 0) {
      const prev = scenes[scenes.length - 1];
      prev.transition = { in: prev.transition?.in ?? transition.out, out: transition.out };
    }
    const offset =
      clip.offsetX !== 0 || clip.offsetY !== 0
        ? { x: clamp(clip.offsetX, -1, 1), y: clamp(clip.offsetY, -1, 1) }
        : undefined;
    const scale = clipScale(clip);
    const trim = kind === 'video' ? Math.max(0, clip.trimStart) : 0;
    const speed = kind === 'video' && hasSpeedChange(clip.speedZones)
      ? coalesceSpeedPieces(windowSpeedPieces(clip.speedZones, trim, clipSourceEnd(clip)))
      : undefined;
    const grade = kind === 'video' ? activeClipGrade(clip.grade) : undefined;
    scenes.push({
      id: clip.id,
      kind,
      start: clip.start,
      length: clip.duration,
      src: media.url,
      fit: scale < 1 ? 'contain' : clip.fit,
      trim,
      transition,
      offset,
      ...(scale < 1 ? { scale } : {}),
      ...(speed && speed.some((piece) => piece.speed !== 1) ? { speed } : {}),
      ...(grade ? { grade } : {}),
      volume: kind === 'video' ? (clip.muted ? 0 : clamp(clip.volume, 0, 1)) : 0,
    });
  }

  const texts: TimelineTextOverlay[] = args.state.texts
    .filter((t) => t.text.trim().length > 0 && t.duration > 0)
    .map((t) => ({
      text: t.text.trim(),
      start: Math.max(0, t.start),
      length: Math.max(MIN_CLIP_SECONDS, t.duration),
      fontSize: t.fontSize,
      color: t.color,
      align: t.align,
      background: t.background,
      x: clamp(t.x, 0, 1),
      y: clamp(t.y, 0, 1),
      motion: overlayMotion(t.motion),
      style: resolveTextStyle(t),
    }));

  const musicUrls = args.musicUrls ?? {};
  const audioDrafts: Array<TimelineAudioClip & { requested: number; voice: boolean }> = [];
  const sourceSeconds = args.sourceSeconds ?? {};
  for (const audio of args.state.audios) {
    let src: string | null = null;
    let name = 'Audio';
    let source: number | null = null;
    const providerProblem = providerAudioProblem(audio);
    if (providerProblem) {
      issues.push({ name: 'Music', message: providerProblem });
      continue;
    }
    const key = musicSourceKey(audio);
    if (key) {
      src = musicUrls[key] ?? null;
      name = 'Library track';
      source = sourceSeconds[key] ?? null;
      if (!src) {
        issues.push({ name, message: 'That music track is no longer available.' });
        continue;
      }
    } else if (audio.assetId) {
      const media = byId.get(audio.assetId);
      src = media?.url ?? null;
      name = media?.name || (audio.role === 'voiceover' ? 'Voiceover' : 'Uploaded audio');
      if (!src) {
        issues.push({
          assetId: audio.assetId,
          name,
          message: `${name} could not be loaded. Remove it or upload it again.`,
        });
        continue;
      }
    }
    if (!src) continue;
    const fitted = clampAudioToSource(audio, source);
    audioDrafts.push({
      src,
      start: Math.max(0, audio.start),
      length: fitted.duration,
      trim: fitted.trimStart,
      volume: clamp(audio.volume, 0, 1),
      ...clampAudioFades(audio.fadeIn, audio.fadeOut, fitted.duration),
      requested: clamp(audio.volume, 0, 1),
      voice: audio.role === 'voiceover',
    });
  }

  const voiceDrafts = audioDrafts.filter((a) => a.voice);
  const bedDrafts = audioDrafts.filter((a) => !a.voice);
  const voiceRanges = mergeRanges(voiceDrafts.map((a) => ({ start: a.start, end: a.start + a.length })));
  const duck = clamp(args.state.voiceDuck ?? DEFAULT_VOICE_DUCK, 0, 1);

  // Music and clip sound share one limit; a voiceover sits on top at its own level.
  const mixSpans: MixSpan[] = [
    ...scenes
      .filter((s) => s.kind === 'video' && (s.volume ?? 0) > 0)
      .map((s) => ({ start: s.start, end: s.start + s.length, volume: s.volume ?? 0 })),
    ...bedDrafts.map((a) => ({ start: a.start, end: a.start + a.length, volume: a.requested })),
  ];
  const limited = limitOverlapVolumes(mixSpans);
  let cursor = 0;
  for (const scene of scenes) {
    if (scene.kind === 'video' && (scene.volume ?? 0) > 0) {
      const level = limited[cursor] ?? scene.volume ?? 0;
      const underVoice = voiceRanges.some((r) => scene.start < r.end && r.start < scene.start + scene.length);
      scene.volume = roundTime(underVoice ? level * duck : level);
      cursor += 1;
    }
  }
  const bedClips: TimelineAudioClip[] = bedDrafts.flatMap((draft, i) => {
    const { requested: _requested, voice: _voice, ...rest } = draft;
    return duckUnderVoice({ ...rest, volume: limited[cursor + i] ?? rest.volume }, voiceRanges, duck);
  });
  const voiceLevels = limitOverlapVolumes(
    voiceDrafts.map((a) => ({ start: a.start, end: a.start + a.length, volume: a.requested })),
  );
  const voiceClips: TimelineAudioClip[] = voiceDrafts.map((draft, i) => {
    const { requested: _requested, voice: _voice, ...rest } = draft;
    return { ...rest, volume: voiceLevels[i] ?? rest.volume };
  });
  const audioClips = [...bedClips, ...voiceClips];

  const totalFromScenes = scenes.reduce((max, s) => Math.max(max, s.start + s.length), 0);
  const totalFromText = texts.reduce((max, t) => Math.max(max, t.start + t.length), 0);
  const totalFromAudio = audioClips.reduce((max, a) => Math.max(max, a.start + a.length), 0);
  const totalLength = roundTime(Math.max(totalFromScenes, totalFromText, totalFromAudio));

  const logo = args.state.logo;
  const footageSpans = footageRanges(scenes);
  const footageStart = footageSpans[0]?.start ?? 0;
  const footageEnd = footageSpans.length
    ? footageSpans[footageSpans.length - 1].start + footageSpans[footageSpans.length - 1].length
    : 0;
  const ir: TimelineIR = {
    templateId: 'freeform',
    format: args.format,
    width: dims.width,
    height: dims.height,
    aspectRatio: dims.aspectRatio,
    background: '#0b1220',
    totalLength,
    scenes,
    texts,
    audioClips: audioClips.length > 0 ? audioClips : undefined,
    logoOverlay:
      logo.visible && logoUrl && footageSpans.length > 0
        ? {
            src: logoUrl,
            start: footageStart,
            length: roundTime(footageEnd - footageStart),
            spans: footageSpans,
            x: clamp(logo.x, 0, 1),
            y: clamp(logo.y, 0, 1),
            scale: clamp(logo.scale, MIN_OVERLAY_LOGO_SCALE, MAX_OVERLAY_LOGO_SCALE),
            motion: overlayMotion(logo.motion),
          }
        : undefined,
  };

  return { ir, issues };
}

export interface ActiveVisual {
  index: number;
  opacity: number;
  /** Seconds into the scene, after its start. Add the scene trim to seek a video. */
  local: number;
  /** Hard mask, used by reveal. */
  clipPath?: string;
  /** Move or scale, used by slide, carousel and zoom. */
  transform?: string;
  /** Soft moving edge, used by wipe. */
  maskImage?: string;
}

export interface TransitionPose {
  opacity: number;
  clipPath?: string;
  transform?: string;
  maskImage?: string;
}

function transitionBase(name: string | undefined): string {
  return (name ?? 'fade').replace(/(Fast|Slow)$/, '');
}

/** fade blends both clips. cover and zoom play on the incoming clip. push moves both. */
function transitionKind(name: string | undefined): 'fade' | 'cover' | 'zoom' | 'push' {
  const base = transitionBase(name);
  if (base === 'zoom') return 'zoom';
  if (base === 'reveal' || base.startsWith('wipe')) return 'cover';
  if (base.startsWith('carousel') || base.startsWith('slide')) return 'push';
  return 'fade';
}

function percent(fraction: number): string {
  return `${Math.round(clamp(fraction, 0, 1) * 1000) / 10}%`;
}

function num(n: number): string {
  return String(Math.round(n * 1000) / 1000);
}

/** Soft edge. `shown` is how much of the clip is opaque, growing along `direction`. */
function sweep(direction: 'right' | 'left', shown: number): string | undefined {
  if (shown >= 0.999) return undefined;
  if (shown <= 0.001) return 'linear-gradient(transparent, transparent)';
  const edge = shown * 100;
  const solid = Math.max(0, edge - 28);
  const towards = direction === 'right' ? 'to right' : 'to left';
  return `linear-gradient(${towards}, #000 ${percent(solid / 100)}, transparent ${percent(edge / 100)})`;
}

function pushTransform(x: number, y: number): string | undefined {
  const parts: string[] = [];
  if (Math.abs(x) >= 0.001) parts.push(`translateX(calc(${num(x)} * 100cqw))`);
  if (Math.abs(y) >= 0.001) parts.push(`translateY(calc(${num(y)} * 100cqh))`);
  return parts.length ? parts.join(' ') : undefined;
}

/**
 * How one side of a join looks `progress` through it (0 at the start, 1 at
 * the end). Names are the Shotstack transitions `transitionPair` emits.
 * The incoming clip is painted over the outgoing one, so a wipe, reveal or
 * zoom only has to move the incoming clip.
 */
export function transitionPose(name: string | undefined, role: 'in' | 'out', progress: number): TransitionPose {
  const p = clamp(progress, 0, 1);
  const base = transitionBase(name);
  if (base === 'wipeRight' || base === 'wipeLeft') {
    if (role === 'out') return { opacity: 1 };
    return { opacity: 1, maskImage: sweep(base === 'wipeLeft' ? 'left' : 'right', p) };
  }
  if (base === 'reveal') {
    if (role === 'out') return { opacity: 1 };
    if (p >= 0.999) return { opacity: 1 };
    return { opacity: 1, clipPath: `inset(0 ${percent(1 - p)} 0 0)` };
  }
  if (base === 'zoom') {
    if (role === 'out') return { opacity: 1 };
    return { opacity: p, transform: `scale(${num(1.35 - 0.35 * p)})` };
  }
  if (base.startsWith('carousel') || base.startsWith('slide')) {
    const vertical = base.includes('Up') || base.includes('Down');
    const positive =
      base.includes('Left') || base.includes('Up')
        ? role === 'in' ? 1 - p : -p
        : role === 'in' ? p - 1 : p;
    const transform = vertical ? pushTransform(0, positive) : pushTransform(positive, 0);
    return { opacity: 1, transform };
  }
  return { opacity: role === 'in' ? p : 1 - p };
}

function mergePose(current: TransitionPose, next: TransitionPose): TransitionPose {
  return {
    opacity: Math.min(current.opacity, next.opacity),
    clipPath: next.clipPath ?? current.clipPath,
    transform: [current.transform, next.transform].filter(Boolean).join(' ') || undefined,
    maskImage: next.maskImage ?? current.maskImage,
  };
}

function layerPose(current: TransitionPose, name: string | undefined, role: 'in' | 'out', progress: number): TransitionPose {
  const kind = transitionKind(name);
  if ((kind === 'cover' || kind === 'zoom') && role === 'out') return current;
  return mergePose(current, transitionPose(name, role, progress));
}

/** Which visual scenes are on screen at `time`, including the join's movement. */
export function activeVisuals(
  scenes: Array<Pick<TimelineScene, 'start' | 'length' | 'transition'>>,
  time: number,
): ActiveVisual[] {
  const hits: ActiveVisual[] = [];
  for (let i = 0; i < scenes.length; i++) {
    const scene = scenes[i];
    const end = scene.start + scene.length;
    if (time < scene.start || time >= end - 0.0001) continue;
    let pose: TransitionPose = { opacity: 1 };
    const prev = scenes[i - 1];
    const next = scenes[i + 1];
    if (prev && scene.start < prev.start + prev.length - 0.001) {
      const overlap = prev.start + prev.length - scene.start;
      if (overlap > 0 && time < scene.start + overlap) {
        pose = layerPose(pose, scene.transition?.in, 'in', clamp((time - scene.start) / overlap, 0, 1));
      }
    }
    if (next && next.start < end - 0.001) {
      const overlap = end - next.start;
      if (overlap > 0 && time >= next.start) {
        pose = layerPose(pose, scene.transition?.out, 'out', clamp((time - next.start) / overlap, 0, 1));
      }
    }
    hits.push({
      index: i,
      opacity: pose.opacity,
      local: time - scene.start,
      clipPath: pose.clipPath,
      transform: pose.transform,
      maskImage: pose.maskImage,
    });
  }
  return hits;
}

// ---------------------------------------------------------------------
// Edits
// ---------------------------------------------------------------------

function replaceClip(state: EditorState, id: string, next: EditorClip): EditorState {
  return { ...state, clips: state.clips.map((c) => (c.id === id ? next : c)) };
}

export interface ClipAssetHint {
  id: string;
  kind: string;
  durationSeconds: number | null;
}

export function insertBlank(
  state: EditorState,
  where: 'start' | 'end',
  options?: { color?: string; showLogo?: boolean },
): EditorState {
  const clip: EditorClip = {
    id: createEditorId('blank'),
    assetId: '',
    blank: true,
    background: options?.color || DEFAULT_BLANK_COLOR,
    showLogo: options?.showLogo !== false,
    logoScale: DEFAULT_PLATE_LOGO_SCALE,
    logoAnimation: 'fadeIn',
    logoMotion: { ...DEFAULT_MOTION },
    trimStart: 0,
    duration: DEFAULT_BLANK_SECONDS,
    fit: 'cover',
    offsetX: 0,
    offsetY: 0,
    transitionIn: 'cut',
    transitionDuration: 0,
    volume: 0,
    muted: true,
  };
  return {
    ...state,
    clips: where === 'start' ? [clip, ...state.clips] : [...state.clips, clip],
  };
}

export function updateLogo(state: EditorState, patch: Partial<EditorLogo>): EditorState {
  const logo = { ...state.logo, ...patch };
  return {
    ...state,
    logo: {
      visible: logo.visible,
      x: clamp(logo.x, 0, 1),
      y: clamp(logo.y, 0, 1),
      scale: roundTime(clamp(logo.scale, MIN_OVERLAY_LOGO_SCALE, MAX_OVERLAY_LOGO_SCALE)),
      ...(logo.motion ? { motion: overlayMotion(logo.motion) } : {}),
    },
  };
}

export function updateBlank(
  state: EditorState,
  clipId: string,
  patch: {
    background?: string;
    showLogo?: boolean;
    logoScale?: number;
    logoAnimation?: LogoAnimationStyle;
    logoMotion?: ElementMotion;
    logoX?: number;
    logoY?: number;
    duration?: number;
  },
): EditorState {
  const current = state.clips.find((clip) => clip.id === clipId && clip.blank);
  if (!current) return state;
  const next: EditorClip = {
    ...current,
    background: patch.background ?? current.background,
    showLogo: patch.showLogo ?? current.showLogo,
    logoScale:
      patch.logoScale == null
        ? current.logoScale
        : roundTime(clamp(patch.logoScale, MIN_PLATE_LOGO_SCALE, MAX_PLATE_LOGO_SCALE)),
    logoAnimation: patch.logoAnimation ?? current.logoAnimation,
    logoMotion: patch.logoMotion ? normaliseMotion(patch.logoMotion) : current.logoMotion,
    logoX: patch.logoX == null ? current.logoX : clamp(patch.logoX, 0, 1),
    logoY: patch.logoY == null ? current.logoY : clamp(patch.logoY, 0, 1),
  };
  const replaced = replaceClip(state, clipId, next);
  if (patch.duration == null) return replaced;
  return setClipTrim(replaced, clipId, 0, patch.duration, null, 'blank');
}

/** Colour, length and logo placement of an opening or end plate. Saved so the next video can reuse it. */
export interface PlateStyle {
  background: string;
  showLogo: boolean;
  logoScale: number;
  logoMotion: ElementMotion;
  logoX: number;
  logoY: number;
  duration: number;
}

/** Read the style of a colour plate. */
export function plateStyleOf(clip: EditorClip): PlateStyle {
  return {
    background: clip.background || DEFAULT_BLANK_COLOR,
    showLogo: clip.showLogo !== false,
    logoScale: clip.logoScale ?? DEFAULT_PLATE_LOGO_SCALE,
    logoMotion: plateLogoMotion(clip),
    logoX: clip.logoX ?? 0.5,
    logoY: clip.logoY ?? 0.5,
    duration: clip.duration,
  };
}

/** Opening is the first clip when it is a plate. End is the last clip when it is a different plate. */
export function brandPlatesOf(state: EditorState): { opening: PlateStyle | null; ending: PlateStyle | null } {
  const first = state.clips[0];
  const last = state.clips[state.clips.length - 1];
  return {
    opening: first?.blank ? plateStyleOf(first) : null,
    ending: last?.blank && last.id !== first?.id ? plateStyleOf(last) : null,
  };
}

/** Accept a plate stored as JSON. Anything that is not an object is ignored. */
export function parsePlateStyle(value: unknown): PlateStyle | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const background = typeof raw.background === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw.background)
    ? raw.background
    : DEFAULT_BLANK_COLOR;
  const duration = Number(raw.duration);
  const logoScale = Number(raw.logoScale);
  const logoX = Number(raw.logoX);
  const logoY = Number(raw.logoY);
  return {
    background,
    showLogo: raw.showLogo !== false,
    logoScale: Number.isFinite(logoScale) ? logoScale : DEFAULT_PLATE_LOGO_SCALE,
    logoMotion: normaliseMotion(raw.logoMotion),
    logoX: Number.isFinite(logoX) ? logoX : 0.5,
    logoY: Number.isFinite(logoY) ? logoY : 0.5,
    duration: Number.isFinite(duration) ? duration : DEFAULT_BLANK_SECONDS,
  };
}

/**
 * Put the saved opening and end onto this timeline.
 * An existing first or last plate is restyled. Missing plates are added.
 * Calling it again updates those plates instead of stacking more.
 */
export function applyBrandPlates(
  state: EditorState,
  plates: { opening?: PlateStyle | null; ending?: PlateStyle | null },
): EditorState {
  let next = state;
  if (plates.opening) {
    if (!next.clips[0]?.blank) next = insertBlank(next, 'start', { color: plates.opening.background });
    next = updateBlank(next, next.clips[0].id, plates.opening);
  }
  if (plates.ending) {
    const openingId = plates.opening && next.clips[0]?.blank ? next.clips[0].id : null;
    const last = next.clips[next.clips.length - 1];
    if (!last?.blank || last.id === openingId) {
      next = insertBlank(next, 'end', { color: plates.ending.background });
    }
    const end = next.clips[next.clips.length - 1];
    next = updateBlank(next, end.id, plates.ending);
  }
  return next;
}

/** Gives plate `toId` the same colour, length and logo settings as plate `fromId`. */
export function copyPlateStyle(state: EditorState, fromId: string, toId: string): EditorState {
  const from = state.clips.find((clip) => clip.id === fromId && clip.blank);
  if (!from || fromId === toId) return state;
  return updateBlank(state, toId, {
    background: from.background,
    showLogo: from.showLogo,
    logoScale: from.logoScale ?? DEFAULT_PLATE_LOGO_SCALE,
    logoAnimation: from.logoAnimation,
    logoMotion: plateLogoMotion(from),
    logoX: from.logoX ?? 0.5,
    logoY: from.logoY ?? 0.5,
    duration: from.duration,
  });
}

export function appendClip(state: EditorState, asset: ClipAssetHint): EditorState {
  const isVideo = asset.kind === 'video';
  const source = asset.durationSeconds && asset.durationSeconds > 0 ? asset.durationSeconds : null;
  const duration = isVideo ? (source ?? DEFAULT_STILL_SECONDS) : DEFAULT_STILL_SECONDS;
  const clip: EditorClip = {
    id: createEditorId('clip'),
    assetId: asset.id,
    trimStart: 0,
    duration: roundTime(Math.max(MIN_CLIP_SECONDS, duration)),
    fit: asset.kind === 'floor_plan' ? 'contain' : 'cover',
    offsetX: 0,
    offsetY: 0,
    transitionIn: 'cut',
    transitionDuration: 0,
    volume: isVideo ? 1 : 0,
    muted: false,
  };
  // A trailing colour plate is the end logo. New photos and clips belong
  // before it, so the end stays at the end. A plate on its own stays first.
  const last = state.clips[state.clips.length - 1];
  const clips = last?.blank && state.clips.length > 1
    ? [...state.clips.slice(0, -1), clip, last]
    : [...state.clips, clip];
  return { ...state, clips };
}

export function moveClip(state: EditorState, clipId: string, toIndex: number): EditorState {
  const from = state.clips.findIndex((c) => c.id === clipId);
  if (from < 0) return state;
  const clips = [...state.clips];
  const [item] = clips.splice(from, 1);
  const index = Math.max(0, Math.min(toIndex, clips.length));
  clips.splice(index, 0, item);
  return { ...state, clips };
}

export function deleteClip(state: EditorState, clipId: string): EditorState {
  return { ...state, clips: state.clips.filter((c) => c.id !== clipId) };
}

export function duplicateClip(state: EditorState, clipId: string): EditorState {
  const index = state.clips.findIndex((c) => c.id === clipId);
  if (index < 0) return state;
  const copy: EditorClip = { ...withGap(state.clips[index], 0), id: createEditorId('clip') };
  const clips = [...state.clips];
  clips.splice(index + 1, 0, copy);
  return { ...state, clips };
}

export function splitClip(state: EditorState, clipId: string, time: number): EditorState {
  const laid = layOutClips(state.clips);
  const index = laid.clips.findIndex((c) => c.id === clipId);
  if (index < 0) return state;
  const laidClip = laid.clips[index];
  const source = state.clips[index];
  const local = time - laidClip.start;
  if (local <= MIN_CLIP_SECONDS || laidClip.duration - local <= MIN_CLIP_SECONDS) return state;
  const left: EditorClip = { ...source, duration: roundTime(local) };
  const splitAt = source.speedZones?.length
    ? speedSourceAt(source.speedZones, source.trimStart, local)
    : source.trimStart + local;
  const right: EditorClip = {
    ...withGap(source, 0),
    id: createEditorId('clip'),
    trimStart: roundTime(splitAt),
    duration: roundTime(laidClip.duration - local),
    transitionIn: 'cut',
    transitionDuration: 0,
  };
  const clips = [...state.clips];
  clips.splice(index, 1, left, right);
  return { ...state, clips };
}

export function setClipTrim(
  state: EditorState,
  clipId: string,
  trimStart: number,
  duration: number,
  sourceDuration: number | null,
  kind: string,
): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current) return state;
  let trim = Math.max(0, trimStart);
  let dur = Math.max(MIN_CLIP_SECONDS, duration);
  if (kind === 'video' && sourceDuration && sourceDuration > 0) {
    trim = Math.min(trim, Math.max(0, sourceDuration - MIN_CLIP_SECONDS));
    const available = current.speedZones?.length
      ? speedOutputSeconds(current.speedZones, trim, sourceDuration)
      : sourceDuration - trim;
    dur = Math.min(dur, Math.max(MIN_CLIP_SECONDS, available));
  } else if (kind !== 'video') {
    trim = 0;
    dur = Math.min(dur, 120);
  }
  return replaceClip(state, clipId, {
    ...current,
    trimStart: roundTime(trim),
    duration: roundTime(dur),
  });
}

/** Source second where a clip's footage ends, after any speed changes. */
export function clipSourceEnd(clip: Pick<EditorClip, 'trimStart' | 'duration' | 'speedZones'>): number {
  const trim = Math.max(0, clip.trimStart);
  return clip.speedZones?.length ? speedSourceAt(clip.speedZones, trim, clip.duration) : trim + clip.duration;
}

/**
 * Replace a video clip's speed zones. The clip keeps showing the same
 * footage, so its length on the timeline grows or shrinks to match.
 * A zone touching the clip's first or last frame is extended past it, so
 * the clip starts or ends at that speed instead of easing into it.
 */
export function setClipSpeedZones(
  state: EditorState,
  clipId: string,
  zones: SpeedZone[],
  sourceDuration: number | null,
): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current || current.blank) return state;
  const trim = Math.max(0, current.trimStart);
  let end = clipSourceEnd(current);
  if (sourceDuration && sourceDuration > 0) end = Math.min(end, sourceDuration);
  const inside = normaliseSpeedZones(
    zones
      .filter((z) => z.end > trim + 0.01 && z.start < end - 0.01)
      .map((z) => ({
        ...z,
        start: z.start <= trim + 0.01 ? 0 : z.start,
        end: z.end >= end - 0.01 ? SPEED_ZONE_OPEN_END : z.end,
      })),
  );
  const { speedZones: _old, ...rest } = current;
  if (inside.length === 0) {
    return replaceClip(state, clipId, { ...rest, duration: roundTime(Math.max(MIN_CLIP_SECONDS, end - trim)) });
  }
  return replaceClip(state, clipId, {
    ...rest,
    speedZones: inside,
    duration: roundTime(Math.max(MIN_CLIP_SECONDS, speedOutputSeconds(inside, trim, end))),
  });
}

/** Play the whole clip at one speed, keeping the same footage. */
export function setClipSpeed(state: EditorState, clipId: string, speed: number, sourceDuration: number | null): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current) return state;
  return setClipSpeedZones(state, clipId, [{ start: 0, end: SPEED_ZONE_OPEN_END, speed }], sourceDuration);
}

/** Add a speed zone on top of a clip's others, cutting it out of any it overlaps. */
export function insertSpeedZone(
  state: EditorState,
  clipId: string,
  zone: SpeedZone,
  sourceDuration: number | null,
): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current) return state;
  const rest = normaliseSpeedZones(current.speedZones).flatMap((z) =>
    z.end <= zone.start || z.start >= zone.end
      ? [z]
      : [
          ...(z.start < zone.start ? [{ ...z, end: zone.start }] : []),
          ...(z.end > zone.end ? [{ ...z, start: zone.end }] : []),
        ],
  );
  return setClipSpeedZones(state, clipId, [...rest, zone], sourceDuration);
}

function withoutSpeed(clip: EditorClip): EditorClip {
  if (!clip.speedZones) return clip;
  const { speedZones: _drop, ...rest } = clip;
  return rest;
}

/**
 * Shorten video clips that run past the end of their footage, e.g. after the
 * real length of a file is measured. Returns `state` itself when nothing changes.
 */
export function fitClipsToSources(state: EditorState, sourceSeconds: ReadonlyMap<string, number>): EditorState {
  let next = state;
  for (const clip of state.clips) {
    if (clip.blank) continue;
    const seconds = sourceSeconds.get(clip.assetId);
    if (!seconds || seconds <= 0) continue;
    const fitted = setClipTrim(next, clip.id, clip.trimStart, clip.duration, seconds, 'video');
    const after = fitted.clips.find((c) => c.id === clip.id);
    if (after && (after.trimStart !== clip.trimStart || after.duration !== clip.duration)) next = fitted;
  }
  return next;
}

function sameGrade(a: ClipGrade | undefined, b: ClipGrade | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Set or clear a video clip's curves and white balance. An unchanged grade is removed. */
export function setClipGrade(state: EditorState, clipId: string, grade: ClipGrade | null): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current) return state;
  const next = grade ? activeClipGrade(grade) : undefined;
  if (sameGrade(activeClipGrade(current.grade), next)) return state;
  if (!next) {
    const { grade: _drop, ...rest } = current;
    return replaceClip(state, clipId, rest);
  }
  return replaceClip(state, clipId, { ...current, grade: next });
}

export function setClipFit(state: EditorState, clipId: string, fit: FitMode): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current) return state;
  return replaceClip(state, clipId, { ...current, fit });
}

export function setClipOffset(state: EditorState, clipId: string, offsetX: number, offsetY: number): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current) return state;
  return replaceClip(state, clipId, {
    ...current,
    offsetX: roundTime(clamp(offsetX, -1, 1)),
    offsetY: roundTime(clamp(offsetY, -1, 1)),
  });
}

export function clipScale(clip: Pick<EditorClip, 'scale'>): number {
  return clip.scale == null ? 1 : clamp(clip.scale, MIN_CLIP_SCALE, 1);
}

export function setClipScale(state: EditorState, clipId: string, scale: number): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current || current.blank) return state;
  const next = roundTime(clamp(scale, MIN_CLIP_SCALE, 1));
  if (next >= 1) {
    const { scale: _drop, ...rest } = current;
    return replaceClip(state, clipId, rest);
  }
  const limit = (1 - next) / 2;
  return replaceClip(state, clipId, {
    ...current,
    scale: next,
    offsetX: roundTime(clamp(current.offsetX, -limit, limit)),
    offsetY: roundTime(clamp(current.offsetY, -limit, limit)),
  });
}

export function setClipTransition(
  state: EditorState,
  clipId: string,
  style: CutStyle,
  duration: number,
): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current) return state;
  return replaceClip(state, clipId, {
    ...current,
    transitionIn: style,
    transitionDuration: style === 'cut' ? 0 : roundTime(clamp(duration || DEFAULT_CROSSFADE_SECONDS, 0.1, 2)),
  });
}

/** Set the original sound of every video clip at once. */
export function setAllClipAudio(state: EditorState, volume: number, muted: boolean): EditorState {
  return {
    ...state,
    clips: state.clips.map((c) => (c.blank ? c : { ...c, volume: roundTime(clamp(volume, 0, 1)), muted })),
  };
}

export function setClipAudio(state: EditorState, clipId: string, volume: number, muted: boolean): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current) return state;
  return replaceClip(state, clipId, {
    ...current,
    volume: roundTime(clamp(volume, 0, 1)),
    muted,
  });
}

export function addText(
  state: EditorState,
  partial: Partial<EditorText> & Pick<EditorText, 'text'>,
): EditorState {
  const duration = visualDuration(state);
  const item: EditorText = {
    ...partial,
    id: partial.id ?? createEditorId('text'),
    role: partial.role ?? 'free',
    text: partial.text,
    start: partial.start ?? 0,
    duration: partial.duration ?? Math.max(DEFAULT_PHOTO_SECONDS, Math.min(4, duration || DEFAULT_PHOTO_SECONDS)),
    fontSize: partial.fontSize ?? 48,
    color: partial.color ?? '#ffffff',
    align: partial.align ?? 'center',
    background: partial.background === undefined ? 'rgba(0,0,0,0.45)' : partial.background,
    x: partial.x ?? 0.5,
    y: partial.y ?? 0.5,
  };
  return { ...state, texts: [...state.texts, item] };
}

export function updateText(state: EditorState, id: string, patch: Partial<EditorText>): EditorState {
  return {
    ...state,
    texts: state.texts.map((t) => (t.id === id ? { ...t, ...patch, id: t.id } : t)),
  };
}

export function deleteText(state: EditorState, id: string): EditorState {
  return { ...state, texts: state.texts.filter((t) => t.id !== id) };
}

export function addAudio(
  state: EditorState,
  source: {
    assetId?: string | null;
    musicId?: string | null;
    durationSeconds: number | null;
    role?: AudioRole;
  },
): EditorState {
  const visual = visualDuration(state);
  if (source.role === 'voiceover') {
    const length = source.durationSeconds && source.durationSeconds > 0 ? source.durationSeconds : visual || 15;
    return {
      ...state,
      audios: [
        ...state.audios,
        {
          id: createEditorId('voice'),
          role: 'voiceover',
          assetId: source.assetId ?? null,
          musicId: null,
          start: 0,
          trimStart: 0,
          duration: roundTime(Math.max(MIN_CLIP_SECONDS, length)),
          volume: 1,
          fadeIn: 0.1,
          fadeOut: 0.3,
        },
      ],
    };
  }
  const duration = Math.max(
    MIN_CLIP_SECONDS,
    source.durationSeconds && source.durationSeconds > 0 ? source.durationSeconds : visual || 15,
  );
  const item: EditorAudio = {
    id: createEditorId('audio'),
    assetId: source.assetId ?? null,
    musicId: source.musicId ?? null,
    start: 0,
    trimStart: 0,
    duration: roundTime(Math.min(duration, visual > 0 ? visual : duration)),
    volume: 0.8,
    fadeIn: 0.4,
    fadeOut: 0.8,
  };
  return { ...state, audios: [...state.audios, item] };
}

export const MAX_AUDIO_FADE_SECONDS = 10;

/** Keeps fades non-negative and stops them overlapping inside a clip of `length` seconds. */
export function clampAudioFades(fadeIn: number, fadeOut: number, length: number): { fadeIn: number; fadeOut: number } {
  let fi = clamp(Number.isFinite(fadeIn) ? fadeIn : 0, 0, MAX_AUDIO_FADE_SECONDS);
  let fo = clamp(Number.isFinite(fadeOut) ? fadeOut : 0, 0, MAX_AUDIO_FADE_SECONDS);
  const room = Math.max(0, length);
  if (fi + fo > room && fi + fo > 0) {
    const k = room / (fi + fo);
    fi *= k;
    fo *= k;
  }
  return { fadeIn: roundTime(fi), fadeOut: roundTime(fo) };
}

export function updateAudio(state: EditorState, id: string, patch: Partial<EditorAudio>): EditorState {
  return {
    ...state,
    audios: state.audios.map((a) => (a.id === id ? { ...a, ...patch, id: a.id } : a)),
  };
}

export function deleteAudio(state: EditorState, id: string): EditorState {
  return { ...state, audios: state.audios.filter((a) => a.id !== id) };
}

/**
 * Remove an asset from the cut. Scenes made from it go; a scene whose
 * generated clip or processed footage is removed falls back to its source.
 */
export function deleteClipsForAsset(state: EditorState, assetId: string): EditorState {
  const clips: EditorClip[] = [];
  for (const clip of state.clips) {
    if (clip.sourceAssetId === assetId) continue;
    if (clip.videoMotion?.sourceAssetId === assetId) continue;
    if (clip.videoMotion && clip.assetId === assetId) {
      const { videoMotion: _videoMotion, ...rest } = withoutSpeed(clip);
      clips.push({ ...rest, assetId: clip.videoMotion.sourceAssetId });
      continue;
    }
    if (clip.assetId === assetId && !clip.sourceAssetId) continue;
    if (clip.animation?.assetId === assetId) {
      const still = clip.animation.active ? revertClipToStill(clip) : clip;
      const { animation: _animation, ...rest } = still;
      clips.push(rest);
      continue;
    }
    clips.push(clip);
  }
  return {
    ...state,
    clips,
    audios: state.audios.filter((a) => a.assetId !== assetId),
  };
}

// ---------------------------------------------------------------------
// ImageMotion scenes
// ---------------------------------------------------------------------

/** The photo a scene shows when it is not animated. */
export function stillAssetId(clip: EditorClip): string {
  return clip.sourceAssetId ?? clip.assetId;
}

export function isAnimatedScene(clip: EditorClip): boolean {
  return Boolean(clip.animation?.active && clip.sourceAssetId && clip.animation.assetId === clip.assetId);
}

/** Record a submitted generation job on a still scene. */
export function attachAnimationJob(state: EditorState, clipId: string, jobId: string): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current || current.blank) return state;
  const base = isAnimatedScene(current) ? revertClipToStill(current) : current;
  return replaceClip(state, clipId, {
    ...base,
    animation: { jobId, assetId: null, active: false },
  });
}

/** Store the finished clip for a job without switching the scene to it. */
export function setAnimationResult(state: EditorState, jobId: string, assetId: string): EditorState {
  let changed = false;
  const clips = state.clips.map((clip) => {
    if (clip.animation?.jobId !== jobId || clip.animation.assetId === assetId) return clip;
    changed = true;
    return { ...clip, animation: { ...clip.animation, assetId } };
  });
  return changed ? { ...state, clips } : state;
}

/** Drop a job reference, e.g. after the job failed. Active animations are kept. */
export function clearAnimationJob(state: EditorState, jobId: string): EditorState {
  let changed = false;
  const clips = state.clips.map((clip) => {
    if (clip.animation?.jobId !== jobId || clip.animation.assetId) return clip;
    changed = true;
    const { animation: _animation, ...rest } = clip;
    return rest;
  });
  return changed ? { ...state, clips } : state;
}

/**
 * Play the generated clip in this scene. The photo stays as
 * `sourceAssetId` and its framing is kept for reverting.
 */
export function applyAnimation(state: EditorState, clipId: string, clipSeconds: number | null): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  const animation = current?.animation;
  if (!current || !animation?.assetId || isAnimatedScene(current)) return state;
  const length = clipSeconds && clipSeconds > 0 ? clipSeconds : current.duration;
  return replaceClip(state, clipId, {
    ...current,
    sourceAssetId: current.assetId,
    assetId: animation.assetId,
    trimStart: 0,
    duration: roundTime(Math.max(MIN_CLIP_SECONDS, Math.min(current.duration, length))),
    // The clip was generated from this framing already.
    fit: 'cover',
    offsetX: 0,
    offsetY: 0,
    volume: 0,
    muted: true,
    animation: {
      ...animation,
      active: true,
      still: { duration: current.duration, fit: current.fit, offsetX: current.offsetX, offsetY: current.offsetY },
    },
  });
}

function revertClipToStill(clip: EditorClip): EditorClip {
  if (!clip.sourceAssetId || !clip.animation) return clip;
  const still = clip.animation.still;
  const { sourceAssetId: _source, ...rest } = withoutSpeed(clip);
  return {
    ...rest,
    assetId: clip.sourceAssetId,
    trimStart: 0,
    duration: still?.duration ?? clip.duration,
    fit: still?.fit ?? clip.fit,
    offsetX: still?.offsetX ?? clip.offsetX,
    offsetY: still?.offsetY ?? clip.offsetY,
    volume: 0,
    muted: false,
    animation: { jobId: clip.animation.jobId, assetId: clip.animation.assetId, active: false },
  };
}

/** Show the original photo again. The generated clip stays on the scene. */
export function revertToStill(state: EditorState, clipId: string): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current || !isAnimatedScene(current)) return state;
  return replaceClip(state, clipId, revertClipToStill(current));
}

/**
 * Still-photo scenes that could be animated: photos (not floor plans or
 * plates) with no animation playing and no job outstanding.
 * `busyJobIds` are jobs still queued or processing.
 */
export function animatableClips(
  state: EditorState,
  assets: Array<{ id: string; kind: string }>,
  busyJobIds: ReadonlySet<string>,
): EditorClip[] {
  const kinds = new Map(assets.map((a) => [a.id, a.kind]));
  return state.clips.filter((clip) => {
    if (clip.blank || isAnimatedScene(clip)) return false;
    if (kinds.get(clip.assetId) !== 'photo') return false;
    if (clip.animation?.assetId) return false;
    if (clip.animation && busyJobIds.has(clip.animation.jobId)) return false;
    return true;
  });
}

/** The parts of a propertycut_jobs row the editor needs. */
export interface SceneJobRef {
  id: string;
  kind: string;
  scene_id: string | null;
  status: string;
  result_asset_ids: string[];
  created_at: string;
}

/**
 * Bring scenes in line with their ImageMotion jobs. Idempotent, so it runs
 * on every poll and after a refresh or an undo. A finished clip that is no
 * longer in the media library is ignored.
 */
export function reconcileSceneJobs(
  state: EditorState,
  jobs: SceneJobRef[],
  assetIds: ReadonlySet<string>,
): EditorState {
  const sceneJobs = jobs.filter((job) => job.kind === 'imagemotion_scene');
  const resultOf = (job: SceneJobRef) => {
    const id = job.status === 'ready' ? job.result_asset_ids[0] : undefined;
    return id && assetIds.has(id) ? id : null;
  };

  let next = state;
  for (const job of sceneJobs) {
    const result = resultOf(job);
    if (result) next = setAnimationResult(next, job.id, result);
    else if (job.status === 'failed') next = clearAnimationJob(next, job.id);
  }

  const latest = new Map<string, SceneJobRef>();
  for (const job of sceneJobs) {
    if (!job.scene_id) continue;
    const seen = latest.get(job.scene_id);
    if (!seen || job.created_at > seen.created_at) latest.set(job.scene_id, job);
  }
  for (const [sceneId, job] of latest) {
    const clip = next.clips.find((c) => c.id === sceneId);
    if (!clip || clip.blank || clip.animation?.jobId === job.id) continue;
    const active = job.status === 'queued' || job.status === 'processing';
    const result = resultOf(job);
    if (!active && !result) continue;
    next = attachAnimationJob(next, sceneId, job.id);
    if (result) next = setAnimationResult(next, job.id, result);
  }
  return next;
}

// ---------------------------------------------------------------------
// VideoMotion scenes
// ---------------------------------------------------------------------

/** Swap uploaded footage for its processed version, keeping the source to revert to. */
export function applyProcessedFootage(
  state: EditorState,
  clipId: string,
  processed: { assetId: string; jobId: string; durationSeconds: number | null },
): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current || current.blank) return state;
  const sourceAssetId = current.videoMotion?.sourceAssetId ?? current.assetId;
  const length = processed.durationSeconds && processed.durationSeconds > 0 ? processed.durationSeconds : current.duration;
  return replaceClip(state, clipId, {
    ...withoutSpeed(current),
    assetId: processed.assetId,
    trimStart: 0,
    duration: roundTime(Math.max(MIN_CLIP_SECONDS, length)),
    videoMotion: { sourceAssetId, jobId: processed.jobId },
  });
}

export function revertToOriginalFootage(
  state: EditorState,
  clipId: string,
  sourceDurationSeconds: number | null,
): EditorState {
  const current = state.clips.find((c) => c.id === clipId);
  if (!current?.videoMotion) return state;
  const { videoMotion: _videoMotion, ...rest } = withoutSpeed(current);
  const length = sourceDurationSeconds && sourceDurationSeconds > 0 ? sourceDurationSeconds : current.duration;
  return replaceClip(state, clipId, {
    ...rest,
    assetId: current.videoMotion.sourceAssetId,
    trimStart: 0,
    duration: roundTime(Math.max(MIN_CLIP_SECONDS, length)),
  });
}

// ---------------------------------------------------------------------
// Legacy projects (no editor_state yet)
// ---------------------------------------------------------------------

export interface LegacyAsset {
  id: string;
  kind: string;
  order_index: number;
  duration_seconds: number | null;
  trim_start_seconds: number | null;
  clip_duration_seconds: number | null;
  original_filename?: string | null;
}

export interface LegacyProjectBits {
  transitionStyle?: string | null;
  listing?: { address?: string; price?: string; description?: string };
  agent?: {
    name?: string;
    phone?: string;
    email?: string;
    logo_url?: string;
    logo_intro?: boolean;
  };
  outputFormat?: string | null;
  templateId?: string | null;
}

export function formatFromLegacy(bits: LegacyProjectBits): RenderFormat {
  if (bits.outputFormat === 'vertical' || bits.outputFormat === 'square' || bits.outputFormat === 'landscape') {
    return bits.outputFormat;
  }
  if (bits.templateId === 'social-reel') return 'vertical';
  if (bits.templateId === 'square-highlight') return 'square';
  return 'landscape';
}

function makeText(
  role: TextRole,
  text: string,
  start: number,
  duration: number,
  fontSize: number,
  x: number,
  y: number,
): EditorText {
  return {
    id: createEditorId('text'),
    role,
    text,
    start,
    duration,
    fontSize,
    color: '#ffffff',
    align: 'center',
    background: 'rgba(0,0,0,0.45)',
    x,
    y,
  };
}

const LEGACY_CUTS: readonly CutStyle[] = [
  'cut',
  'crossfade',
  'dissolve',
  'wipe',
  'slide',
  'zoom',
  'reveal',
  'carousel',
];

/** Keep the project's chosen join. Unknown older values still crossfade. */
function legacyCut(style: string | null | undefined): CutStyle {
  if (style && (LEGACY_CUTS as readonly string[]).includes(style)) return style as CutStyle;
  return style ? 'crossfade' : 'cut';
}

/**
 * Open a pre-editor project as a freeform cut. Media order, trims and
 * durations are kept. Empty listing fields do not become title cards.
 */
export function editorStateFromLegacy(assets: LegacyAsset[], bits: LegacyProjectBits): EditorState {
  const state = emptyEditorState();
  const visual = assets
    .filter((a) => a.kind === 'photo' || a.kind === 'video' || a.kind === 'floor_plan')
    .sort((a, b) => a.order_index - b.order_index);
  const style = legacyCut(bits.transitionStyle);

  for (const asset of visual) {
    const trim = Math.max(0, asset.trim_start_seconds ?? 0);
    let duration = DEFAULT_PHOTO_SECONDS;
    if (asset.kind === 'video') {
      const source = asset.duration_seconds ?? 0;
      const available = source > trim ? source - trim : DEFAULT_PHOTO_SECONDS;
      const chosen =
        asset.clip_duration_seconds && asset.clip_duration_seconds > 0
          ? asset.clip_duration_seconds
          : available;
      duration = Math.min(chosen, available);
    } else if (asset.clip_duration_seconds && asset.clip_duration_seconds > 0) {
      duration = asset.clip_duration_seconds;
    }
    state.clips.push({
      id: createEditorId('clip'),
      assetId: asset.id,
      trimStart: asset.kind === 'video' ? roundTime(trim) : 0,
      duration: roundTime(Math.max(MIN_CLIP_SECONDS, duration)),
      fit: asset.kind === 'floor_plan' ? 'contain' : 'cover',
      offsetX: 0,
      offsetY: 0,
      transitionIn: state.clips.length > 0 && style !== 'cut' ? style : 'cut',
      transitionDuration: state.clips.length > 0 && style !== 'cut' ? DEFAULT_CROSSFADE_SECONDS : 0,
      volume: asset.kind === 'video' ? 1 : 0,
      muted: false,
    });
  }

  const address = bits.listing?.address?.trim();
  if (address) state.texts.push(makeText('address', address, 0, 3, 42, 0.5, 0.18));
  const price = bits.listing?.price?.trim();
  if (price) state.texts.push(makeText('price', price, 0, 3, 32, 0.5, 0.3));
  const agentLine = [bits.agent?.name, bits.agent?.phone, bits.agent?.email].map((s) => s?.trim()).filter(Boolean).join(' · ');
  if (agentLine) {
    const dur = visualDuration(state);
    const start = Math.max(0, dur - 3);
    state.texts.push(makeText('agent', agentLine, start, Math.min(3, dur || 3), 28, 0.5, 0.84));
  }
  if (bits.agent?.logo_intro && bits.agent.logo_url) {
    state.logo = { ...state.logo, visible: true };
  }

  for (const asset of assets.filter((a) => a.kind === 'audio')) {
    state.audios.push({
      id: createEditorId('audio'),
      assetId: asset.id,
      musicId: null,
      start: 0,
      trimStart: Math.max(0, asset.trim_start_seconds ?? 0),
      duration: roundTime(Math.max(MIN_CLIP_SECONDS, asset.duration_seconds ?? 15)),
      volume: 0.8,
      fadeIn: 0.4,
      fadeOut: 0.8,
    });
  }

  return state;
}

/** Lift projects saved with the old 16% corner mark onto the larger default. */
function normaliseLogo(raw: unknown, base: EditorLogo): EditorLogo {
  if (!raw || typeof raw !== 'object') return { ...base };
  const logo = { ...base, ...(raw as Partial<EditorLogo>) };
  if (logo.scale === 0.16) {
    logo.scale = base.scale;
    if (logo.x === 0.86) logo.x = base.x;
    if (logo.y === 0.88) logo.y = base.y;
  }
  return logo;
}

export function parseEditorState(value: unknown): EditorState | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Partial<EditorState>;
  if (raw.version !== 1 || !Array.isArray(raw.clips)) return null;
  const base = emptyEditorState();
  return {
    version: 1,
    clips: raw.clips.filter((c) => c && typeof c.id === 'string' && typeof c.assetId === 'string'),
    texts: Array.isArray(raw.texts) ? raw.texts.filter((t) => t && typeof t.id === 'string') : [],
    audios: Array.isArray(raw.audios) ? raw.audios.filter((a) => a && typeof a.id === 'string') : [],
    logo: normaliseLogo(raw.logo, base.logo),
    ...(typeof raw.voiceDuck === 'number' ? { voiceDuck: clamp(raw.voiceDuck, 0, 1) } : {}),
  };
}

export function historyInit(present: EditorState): EditorHistory {
  return { past: [], present, future: [] };
}

export function historyCommit(history: EditorHistory, next: EditorState): EditorHistory {
  if (next === history.present) return history;
  return {
    past: [...history.past, history.present].slice(-80),
    present: next,
    future: [],
  };
}

export function historyUndo(history: EditorHistory): EditorHistory {
  if (history.past.length === 0) return history;
  const present = history.past[history.past.length - 1];
  return {
    past: history.past.slice(0, -1),
    present,
    future: [history.present, ...history.future],
  };
}

export function historyRedo(history: EditorHistory): EditorHistory {
  if (history.future.length === 0) return history;
  const [present, ...future] = history.future;
  return {
    past: [...history.past, history.present],
    present,
    future,
  };
}

/** Clip under the playhead, preferring the incoming clip during a crossfade. */
export function clipAtTime(state: EditorState, time: number): LaidOutClip | null {
  const laid = layOutClips(state.clips);
  const hits = laid.clips.filter((c) => time >= c.start && time < c.start + c.duration - 0.0001);
  return hits[hits.length - 1] ?? null;
}
